const htmlToImage = require('html-to-image');

import {
    addLog,
    nextFrame
} from './utils.js';

const DESKTOP_BREAKPOINT = 992;

let latinFontCss = null;

/**
 * The page's web fonts, inlined for the capture: without them the text is drawn in a fallback font into
 * boxes measured for Manrope and runs over its neighbours. Google serves each weight in six script
 * subsets (over a megabyte inlined), and the checkout is Latin, so only the Latin faces are kept.
 */
async function fontEmbedCss(node) {
    if (latinFontCss === null) {
        try {
            const css = await htmlToImage.getFontEmbedCSS(node);
            latinFontCss = [...new Set(css.split(/(?=@font-face)/).filter(rule => /U\+0+-0*FF\b/i.test(rule)))].join('\n');
        } catch (e) {
            addLog('screenshot fonts error', { error: e.message || String(e) });
            return '';
        }
    }
    return latinFontCss;
}

/**
 * Cleanup previous screenshot data to free memory
 */
function cleanupScreenShot() {
    if (!window.screenShot) return;

    // Revoke object URL if it was created
    if (window.screenShot.objectUrl) {
        try {
            URL.revokeObjectURL(window.screenShot.objectUrl);
        } catch (e) {
            // ignore
        }
    }

    // Clear blob reference to help GC
    window.screenShot.image = null;
    window.screenShot = null;
}

function pickBestMime(canvas) {
    try {
        if (canvas.toDataURL('image/webp').startsWith('data:image/webp')) return 'image/webp';
    } catch {
    }
    return 'image/jpeg';
}

function optimizeImageSize(canvas, mime, targetSizeKB = 300) {
    const targetSizeBytes = targetSizeKB * 1024;
    const quality = mime === 'image/jpeg' ? 0.8 : 0.65;

    return new Promise((resolve) => {
        if (!canvas || !canvas.width || !canvas.height) {
            resolve(null);
            return;
        }

        canvas.toBlob((initialBlob) => {
            if (!initialBlob || initialBlob.size <= targetSizeBytes) {
                resolve(initialBlob);
                return;
            }

            const reducedQuality = quality / 2;
            canvas.toBlob((reducedBlob) => {
                resolve(reducedBlob || initialBlob);
            }, mime, reducedQuality);
        }, mime, quality);
    });
}

async function generateScreenShot(extraCss = "", opts = {}) {
    // Cleanup previous screenshot to free memory
    cleanupScreenShot();

    // Start with clean state
    window.screenShot = {};
    window.screenShot.started = Date.now();

    let wrapper = null;

    try {
        const { ratio = 2 } = opts;
        let pixelRatio = ratio;

        const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
        const isMobileDevice = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);

        if (isSafari || isMobileDevice) {
            pixelRatio = Math.min(pixelRatio, 1);
        }

        const node = document.getElementById('app');
        if (!node) {
            throw new Error('#app not found');
        }

        const rect = node.getBoundingClientRect();
        const viewportWidth = window.innerWidth || document.documentElement.clientWidth || 0;
        const shouldSyncHeight = viewportWidth > DESKTOP_BREAKPOINT;

        // Check if #pay-block is position:fixed (sidebar layout on price/packets pages).
        // On the simple checkout page #pay-block is inline — repositioning it would break the screenshot.
        const realPayBlock = shouldSyncHeight ? document.getElementById('pay-block') : null;
        const payBlockIsFixed = realPayBlock
            ? getComputedStyle(realPayBlock).position === 'fixed'
            : false;

        // Measure left offset only for fixed pay-block (sidebar), so we can recreate its position in the clone.
        const payBlockLeft = payBlockIsFixed
            ? realPayBlock.getBoundingClientRect().left - rect.left
            : null;

        const payInNode = document.querySelector('.pay-in');
        const payInHeight = shouldSyncHeight && payInNode
            ? payInNode.getBoundingClientRect().height
            : 0;

        wrapper = document.createElement('div');
        Object.assign(wrapper.style, {
            position: 'fixed',
            left: '0', top: '0',
            opacity: '0',
            pointerEvents: 'none',
            zIndex: '-1',
            overflow: 'visible'
        });

        const clone = node.cloneNode(true);

        clone.querySelectorAll('#bottom-pay, #card-element, #payment-element, .StripeElement, #google-pay-button button, iframe')
            .forEach(el => el.remove());

        const payBlockClone = clone.querySelector('#pay-block');
        if (payBlockClone) {
            Object.assign(payBlockClone.style, {
                display: 'block',
                visibility: 'visible',
                opacity: '1'
            });
        }

        const scope = `snap-${Math.random().toString(36).slice(2)}`;
        clone.classList.add(scope);

        const styleTag = document.createElement('style');
        const payTypeEl = document.getElementById('pay-type');
        const payType = (payTypeEl && payTypeEl.value) || 'pp';
        const tickSvg = encodeURIComponent(
            `<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 16 16'>
       <path fill='white' d='M6.1 11.2L3.2 8.3 2.1 9.4l4 4 7.8-7.8-1.1-1.1z'/>
     </svg>`
        );
        // Inline radio SVGs as base64 so html-to-image can render them
        // (external url() in content: property fails in canvas capture; url-encoded SVG is also unreliable)
        const radioOffSvg = btoa(
            `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 16 16" width="16" height="16">` +
            `<circle cx="8" cy="8" r="7" fill="none" stroke="#aaa" stroke-width="1.5"/>` +
            `</svg>`
        );
        const radioOnSvg = btoa(
            `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 16 16" width="16" height="16">` +
            `<circle cx="8" cy="8" r="7" fill="none" stroke="#1a73e8" stroke-width="1.5"/>` +
            `<circle cx="8" cy="8" r="4" fill="#1a73e8"/>` +
            `</svg>`
        );
        // Override #pay-block position only on pages where it is position:fixed (sidebar layout).
        // On the simple checkout page the block is inline — we must not touch its position.
        const payBlockPositionCss = payBlockIsFixed
            ? (shouldSyncHeight
                ? `position:absolute!important;`
                : `position:static!important; width:100%!important;`)
            : '';

        styleTag.textContent = `
    .${scope}{ background:#fff; min-height:auto!important; overflow:visible!important; }
    .${scope} .agree-option__label::before,
    .${scope} .agree-option__input:checked + .agree-option__label::before,
    .${scope} .custom-control-input:checked~.custom-control-label:before {
        background-image: url("data:image/svg+xml,${tickSvg}") !important;
        background-position:center !important;
        background-size:16px 16px !important;
    }
    .${scope} .p-type::before { content: url("data:image/svg+xml;base64,${radioOffSvg}") !important; }
    .${scope} .p-type.active::before { content: url("data:image/svg+xml;base64,${radioOnSvg}") !important; }
    .${scope} #pay-block{ display:block!important; visibility:visible!important; opacity:1!important; ${payBlockPositionCss} }
    .${scope} #pay-btn{ display:block!important; visibility:visible!important; position:static!important;}
    .${scope} .screenshot-hide{display:none!important;}
    .${scope} #spinner, .${scope} #card-element{display:none!important;}
    .${scope} .packet__name, .${scope} .pack__not-found-name { white-space: nowrap; }
    .${scope} .main-nav__btns a { white-space: nowrap; }
    .${scope} .pack__not-found-price { white-space: nowrap; font-weight: 700; font-size: 20px; }
    .${scope} #payment-dummy{display:${payType !== 'pp' ? 'flex' : 'none'}!important;margin:10px 0;}
    .${scope} input::placeholder { opacity: 0 !important; color: #fff; }
    .${scope} #faqAccordion span { white-space: nowrap; }
    ${extraCss || ''}
  `;

        if (isSafari) {
            styleTag.textContent += `
              .${scope}.app-nf * { box-shadow: none !important; }
              .${scope}.app-nf .pack { border: 1px solid rgba(0, 0, 0, 0.12) }
              .${scope}.app-nf .pack.active { border: 2px solid #1a73e8 }
            `;
        }

        clone.insertBefore(styleTag, clone.firstChild);
        wrapper.appendChild(clone);
        document.body.appendChild(wrapper);

        // Wait for fonts with timeout (ignore errors)
        await Promise.race([
            document.fonts?.ready || Promise.resolve(),
            new Promise(r => setTimeout(r, 2000))
        ]);

        await nextFrame();

        // Wait for images in clone to load (fixes Norton/secure-logos not appearing in screenshot)
        const cloneImages = clone.querySelectorAll('img[src]');
        const IMAGE_LOAD_TIMEOUT_MS = 2000;
        if (cloneImages.length > 0) {
            await Promise.all(Array.from(cloneImages).map((img) => {
                if (img.complete) return Promise.resolve();
                return Promise.race([
                    new Promise((resolve) => {
                        img.onload = resolve;
                        img.onerror = resolve;
                    }),
                    new Promise((resolve) => setTimeout(resolve, IMAGE_LOAD_TIMEOUT_MS))
                ]);
            }));
            await nextFrame();
        }

        // Expand scrollable containers
        clone.querySelectorAll('#pay-block, .pay-hold, .pay-id').forEach(el => {
            const cs = getComputedStyle(el);
            const isScrollY = cs.overflowY === 'auto' || cs.overflowY === 'scroll';
            if (isScrollY && el.scrollHeight > el.clientHeight + 1) {
                el.style.overflowY = 'visible';
                el.style.maxHeight = 'none';
                el.style.height = el.scrollHeight + 'px';
            }
        });

        let w = rect.width;
        let h = clone.scrollHeight;

        // Sync height with .pay-in on desktop
        if (shouldSyncHeight && payInHeight) {
            const clonedPayIn = clone.querySelector('.pay-in');
            const clonedPayInHeight = clonedPayIn?.getBoundingClientRect().height || payInHeight;
            if (clonedPayInHeight) {
                h = Math.max(clonedPayInHeight, h);
                clone.style.minHeight = `${h}px`;
            }
        }

        const maxHeight = 5000;
        if (h > maxHeight) {
            h = maxHeight;
        }

        // On desktop: #pay-block has position:fixed in CSS — it has intermediate positioned ancestors,
        // so right/left via CSS are unreliable. Instead we use the measured left offset from the real DOM
        // (viewport-relative getBoundingClientRect minus #app left = offset within #app).
        // top:0 because we want the block at the top of the clone, matching the fixed top:0 visual.
        if (shouldSyncHeight && payBlockLeft !== null) {
            const payBlockEl = clone.querySelector('#pay-block');
            if (payBlockEl) {
                // Move pay-block to be a direct child of #app clone so that
                // position:absolute top/left are relative to #app, not an intermediate ancestor.
                clone.appendChild(payBlockEl);
                Object.assign(payBlockEl.style, {
                    position: 'absolute',
                    top: '0',
                    left: payBlockLeft + 'px',
                    right: 'auto',
                    height: h + 'px',
                });
            }
        }

        Object.assign(clone.style, {
            position: 'relative',
            width: w + 'px',
            height: h + 'px',
            maxWidth: 'none',
            maxHeight: 'none',
            boxSizing: 'border-box',
            overflow: 'visible'
        });

        const currentHost = window.location.hostname;
        const filter = (n) => {
            if (!(n instanceof Element)) return true;
            if (n.tagName === 'IFRAME') return false;
            if (n.id === 'card-element' || n.id === 'payment-element') return false;
            if (n.classList?.contains('StripeElement')) return false;
            // Skip cross-origin images — html-to-image fetch() would fail with CORS error
            if (n.tagName === 'IMG' && n.src) {
                try {
                    const url = new URL(n.src);
                    if (url.hostname !== currentHost) return false;
                } catch (_) { /* relative URL — keep it */ }
            }
            return true;
        };

        const fontEmbedCSS = await fontEmbedCss(clone);
        const canvas = await htmlToImage.toCanvas(clone, {
            filter,
            fontEmbedCSS,
            backgroundColor: '#fff',
            pixelRatio: pixelRatio,
            cacheBust: false,
            width: w,
            height: h,
            imagePlaceholder: 'data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///ywAAAAAAQABAAACAUwAOw==',
            // Prevent image load errors from rejecting the entire canvas promise.
            // Without this, onerror fires with a DOM Event (no .message), causing
            // the catch block to log {error: undefined} and skip the screenshot.
            onImageErrorHandler: () => {},
        });

        const mime = pickBestMime(canvas);
        const optimizedImage = await optimizeImageSize(canvas, mime, 300);

        if (!optimizedImage || optimizedImage.size === 0) {
            throw new Error('optimizeImageSize returned empty blob');
        }

        window.screenShot.image = optimizedImage;
        window.screenShot.mime = mime;
        window.screenShot.ext = mime === 'image/webp' ? 'webp' : 'jpg';
        window.screenShot.completed = Date.now();
    } catch (e) {
        window.screenShot = window.screenShot || {};
        window.screenShot.error = e.message || String(e);
        addLog('generateScreenShot error', { error: e.message });
    } finally {
        wrapper?.remove();
    }
}

export {
    generateScreenShot,
    cleanupScreenShot
}
